import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useBrandFilter } from '@/components/brand-filter/brand-filter-context';
import {
  localHoldLeadOf,
  supportsOf,
  type DeliveryDispatchMode,
  type DeliveryProviderSupports,
  type DeliveryProviderType,
} from '@/lib/delivery-providers-client';
import {
  deliveriesClient,
  isDeliveryFeatureUnavailable,
  retryUnlessNotFound,
  type DeliveryCapability,
} from '@/lib/deliveries-client';
import type { LocationResponseDto } from '@/types/brand';

/**
 * The settings tab's brand-scoped config read (`GET /delivery-providers/:brandId`). Only the tab uses
 * it: that route answers 423 for aggregate sessions and 404 for other brands, so the board and the
 * order panel read {@link useDeliveryCapabilities} and the locations' own `delivery` instead.
 */
export const DELIVERY_PROVIDERS_QUERY_ROOT = 'delivery-providers';

export function deliveryProvidersQueryKey(brandId: string) {
  return [DELIVERY_PROVIDERS_QUERY_ROOT, brandId] as const;
}

/** Shared by the board and every open order panel; the settings tab invalidates it on change. */
export const DELIVERY_CAPABILITIES_QUERY_KEY = ['delivery-capabilities'] as const;

/** A provider is connected (or a branch linked) from settings, not mid-shift. */
const STALE_TIME_MS = 5 * 60_000;

/**
 * The branches this session can call riders for, with the provider flags (`GET /deliveries/capabilities`,
 * aggregate-aware). A failed read is an empty list: the board falls back to the locations' `delivery`.
 */
export function useDeliveryCapabilities(enabled = true) {
  return useQuery({
    queryKey: DELIVERY_CAPABILITIES_QUERY_KEY,
    queryFn: () => deliveriesClient.capabilities(),
    // A 404 (an API without the route) turns the read off for the session — see `guardFeature`.
    enabled: enabled && !isDeliveryFeatureUnavailable('capabilities'),
    staleTime: STALE_TIME_MS,
    retry: retryUnlessNotFound,
  });
}

/**
 * A branch as the delivery surfaces need it: a line session's `LocationResponseDto` (which carries its
 * own `delivery` link) or an aggregate session's brand-filter branch (which does not — there the
 * capabilities decide alone).
 */
export type DeliveryScopeLocation = Pick<LocationResponseDto, 'id' | 'name' | 'brandId'> & {
  delivery?: LocationResponseDto['delivery'];
};

/**
 * The branches an orders surface offers riders for. A line session hands its own branches in; an
 * aggregate session's page gets none (the app loads one brand's locations only), so the header's
 * brand filter supplies them. Either way narrowed to the page's pinned branch, when it has one.
 */
export function useDeliveryLocations(
  locations: readonly DeliveryScopeLocation[],
  pinnedLocationId?: string,
): readonly DeliveryScopeLocation[] {
  const { isAggregate, scopedLocations } = useBrandFilter();
  return useMemo(() => {
    const source: readonly DeliveryScopeLocation[] = isAggregate ? scopedLocations : locations;
    return pinnedLocationId ? source.filter((location) => location.id === pinnedLocationId) : source;
  }, [isAggregate, scopedLocations, locations, pinnedLocationId]);
}

/** A branch that can call a rider right now, with what the dialogs need from its provider. */
export interface BoundLocation {
  locationId: string;
  locationName: string | null;
  brandId: string;
  /** The provider's `type` — read through the registry (`deliveryProviderDefinition`), never compared. */
  providerType: DeliveryProviderType;
  dispatchMode: DeliveryDispatchMode;
  /** The brand has its own fleet on the provider. */
  myFleet: boolean;
  /** `terminal` can travel for this branch. */
  supportsTerminal: boolean;
  /** The branch's effective prep time, when known — the rider dialogs start on it. */
  prepTimeMinutes: number | null;
  /** What the provider supports (the capability row's `capabilities`, else the registry). */
  supports: DeliveryProviderSupports;
  /** Minutes before the order is ready Menteo asks for the courier — 0 for a provider that holds itself. */
  courierLeadMinutes: number;
}

/**
 * The linked, enabled branches among `locations`. The capability row is authoritative once loaded
 * (it follows settings changes without a reload); until then — or when that read failed — the
 * location's own `delivery` link decides, with the fleet flags off.
 */
export function boundLocationsFrom(
  locations: readonly DeliveryScopeLocation[],
  capabilities: readonly DeliveryCapability[] | undefined,
): BoundLocation[] {
  const byLocation = new Map((capabilities ?? []).map((capability) => [capability.locationId, capability] as const));
  const bound: BoundLocation[] = [];
  for (const location of locations) {
    const capability = byLocation.get(location.id);
    const link = location.delivery ?? null;
    if (capability) {
      if (!capability.enabled) continue;
      bound.push({
        locationId: location.id,
        locationName: location.name ?? null,
        brandId: location.brandId,
        providerType: capability.provider,
        dispatchMode: capability.dispatchMode,
        myFleet: capability.myFleet,
        supportsTerminal: capability.supportsTerminal,
        prepTimeMinutes: capability.prepTimeMinutes ?? null,
        supports: supportsOf(capability),
        courierLeadMinutes: localHoldLeadOf(capability),
      });
      continue;
    }
    // No capability row: trust the location's link only when the capabilities have not answered.
    if (capabilities !== undefined || !link || link.enabled === false) continue;
    bound.push({
      locationId: location.id,
      locationName: location.name ?? null,
      brandId: location.brandId,
      providerType: link.provider as DeliveryProviderType,
      dispatchMode: link.dispatchMode,
      myFleet: false,
      supportsTerminal: false,
      prepTimeMinutes: null,
      supports: supportsOf(link.provider),
      courierLeadMinutes: localHoldLeadOf(link.provider),
    });
  }
  return bound;
}
